import React, { useContext, useState } from "react";
import {
  TextField,
  Button,
  Typography,
  Box,
  IconButton,
  InputAdornment,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Link,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { CREATE_USER, CURRENT_USER } from "../../graphql/gqlUser";
import { UserContext } from "../../contexts/userContext";
import { SnackbarContext } from "../../contexts/snackbarContext";
import Disclaimer from "../../components/buildingBlocks/Disclaimer";
import { T } from "../../components/T";
import AuthWrapper from "../../components/buildingBlocks/auth-wrapper";
import { Organisations, orgData } from "../../data/organisations";

const Organisation = ({ country, name }: { country: string; name: string }) => (
  <Box display="flex" alignItems="center">
    <TextFlag country={country} />
    {name}
  </Box>
);

const TextFlag = ({ country }: { country: string }) => (
  <Box mr={1}>
    <img
      src={`https://flagcdn.com/16x12/${country}.png`}
      alt={country}
      height="12"
    />
  </Box>
);

const RegisterPage = () => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const [organisation, setOrganisation] = useState("");
  const [customOrganisation, setCustomOrganisation] = useState("");
  const [agreeDisclaimer, setAgreeDisclaimer] = useState(false);
  const [openDisclaimer, setOpenDisclaimer] = useState(false);
  const [hasReadDisclaimer, setHasReadDisclaimer] = useState(false);

  const { token } = useParams();
  const navigate = useNavigate();
  const userContext = useContext(UserContext);
  const { openErrorSnackbar } = useContext(SnackbarContext);
  const [createUser] = useMutation(CREATE_USER);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setConfirmPasswordError(false);

    if (!firstName)
      return openErrorSnackbar(<T str="First name is required" />);
    if (!lastName) return openErrorSnackbar(<T str="Last name is required" />);
    if (!password) return openErrorSnackbar(<T str="Password is required" />);
    if (password !== confirmPassword) {
      setConfirmPasswordError(true);
      return openErrorSnackbar(<T str="Passwords do not match" />);
    }
    if (!agreeDisclaimer) {
      return openErrorSnackbar(<T str="You must agree with the disclaimer" />);
    }
    if (!hasReadDisclaimer) {
      return openErrorSnackbar(<T str="You have not read the disclaimer" />);
    }

    const chosenOrganisation =
      organisation === Organisations.OTHER
        ? customOrganisation || Organisations.OTHER
        : organisation;

    setLoading(true);
    await createUser({
      variables: {
        token,
        password,
        firstName,
        lastName,
        organisation: chosenOrganisation,
      },
      refetchQueries: [{ query: CURRENT_USER }],
    })
      .then(() => setLoading(false))
      .catch((error: Error) => {
        openErrorSnackbar(error.message);
        setLoading(false);
      });
  };

  if (userContext.loading) return <div />;
  if (userContext.user.id !== "") return <Navigate to="/" />;

  const registerableOrganisations = [
    Organisations.BELSGV,
    Organisations.BELFOS,
    Organisations.BELCHI,
    Organisations.BELKSA,
    Organisations.BELKLJ,

    Organisations.NLDSCO,
  ];

  return (
    <AuthWrapper title="Sign Up">
      <Box>
        <Box>
          <TextField
            required
            fullWidth
            label={<T str="First name" />}
            margin="normal"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            color="secondary"
          />

          <TextField
            required
            fullWidth
            label={<T str="Last name" />}
            margin="normal"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            color="secondary"
          />

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel color="secondary">Organisation</InputLabel>
            <Select
              value={organisation}
              label="Organisation"
              onChange={(e) => setOrganisation(e.target.value)}
              color="secondary"
            >
              <MenuItem value={Organisations.NONE}>
                <em>None</em>
              </MenuItem>
              <MenuItem value={Organisations.OTHER}>
                <em>Not in the list</em>
              </MenuItem>
              {registerableOrganisations.map((org) => (
                <MenuItem key={org} value={org}>
                  <Organisation
                    country={orgData[org].country}
                    name={orgData[org].name}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {organisation === Organisations.OTHER && (
            <Box sx={{ ml: 4, mt: 1 }}>
              <TextField
                fullWidth
                label="Add new organisation"
                value={customOrganisation}
                onChange={(e) => setCustomOrganisation(e.target.value)}
                color="secondary"
              />
            </Box>
          )}

          <TextField
            required
            fullWidth
            label="Password"
            type={showPassword ? "text" : "password"}
            margin="normal"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            color="secondary"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            required
            fullWidth
            label="Confirm Password"
            type={showConfirmPassword ? "text" : "password"}
            margin="normal"
            error={confirmPasswordError}
            value={confirmPassword}
            color="secondary"
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              setConfirmPasswordError(false);
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={agreeDisclaimer}
                onChange={(e) => setAgreeDisclaimer(e.target.checked)}
                color="secondary"
              />
            }
            label={
              <Typography variant="body2">
                I have read and agree to{" "}
                <Link
                  component="button"
                  onClick={() => {
                    setOpenDisclaimer(true);
                  }}
                  underline="always"
                  color="secondary"
                  fontSize="0.9rem"
                >
                  this disclaimer
                </Link>
                .
              </Typography>
            }
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="secondary"
            sx={{ mt: 2 }}
            disabled={loading}
            onClick={handleSubmit}
          >
            {loading ? "Signing up..." : "Sign Up"}
          </Button>

          <Grid container justifyContent="center" sx={{ mt: 1 }}>
            <Grid
              item
              sx={{
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
                mt: 1,
              }}
              onClick={() => navigate("/login")}
            >
              Already have an account? Sign In
            </Grid>
          </Grid>
        </Box>

        <Disclaimer
          openDisclaimer={openDisclaimer}
          toggleDisclaimer={() => {
            setOpenDisclaimer(!openDisclaimer);
            setHasReadDisclaimer(true);
          }}
        />
      </Box>
    </AuthWrapper>
  );
};

export default RegisterPage;
